import { forwardRef, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/app/shared/lib/cn';

const OVERFLOW_THRESHOLD = 16;
const THUMB_HEIGHT = 90;
const TRACK_GAP = 16;

interface CustomScrollbarProps extends PropsWithChildren {
  canScroll: boolean;
  maxHeight?: number;
  className?: string;
  trackClassName?: string;
  thumbClassName?: string;
  resetKey?: string | number;
}

export const CustomScrollbar = forwardRef<HTMLDivElement, CustomScrollbarProps>(
  function CustomScrollbar(
    { children, canScroll, maxHeight, className, trackClassName, thumbClassName, resetKey },
    ref,
  ) {
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const drag = useRef<{ y: number; top: number } | null>(null);
    const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });

    const bindRef = useCallback(
      (node: HTMLDivElement | null) => {
        nodeRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const syncThumb = useCallback(() => {
      const el = nodeRef.current;
      if (!el) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const track = Math.max(clientHeight - TRACK_GAP, 0);
      const show = scrollHeight > clientHeight + OVERFLOW_THRESHOLD && track > 0;

      if (!show) {
        setThumb((prev) => (prev.visible ? { top: 0, height: 0, visible: false } : prev));
        return;
      }

      const height = Math.min(THUMB_HEIGHT, track);
      const maxTop = Math.max(track - height, 0);
      const top = maxTop === 0 ? 0 : (scrollTop / (scrollHeight - clientHeight)) * maxTop;

      setThumb((prev) =>
        prev.top === top && prev.height === height && prev.visible ? prev : { top, height, visible: true },
      );
    }, []);

    useEffect(() => {
      if (!canScroll) {
        setThumb({ top: 0, height: 0, visible: false });
        return;
      }
      syncThumb();
    }, [canScroll, maxHeight, resetKey, syncThumb]);

    useEffect(() => {
      const el = nodeRef.current;
      if (!el || !canScroll) return;

      el.addEventListener('scroll', syncThumb, { passive: true });
      return () => el.removeEventListener('scroll', syncThumb);
    }, [canScroll, resetKey, syncThumb]);

    useEffect(() => {
      const onMove = (event: MouseEvent) => {
        const el = nodeRef.current;
        const start = drag.current;
        if (!el || !start) return;

        const track = Math.max(el.clientHeight - TRACK_GAP, 0);
        const height = Math.min(THUMB_HEIGHT, track);
        const maxTop = Math.max(track - height, 0);
        if (maxTop <= 0) return;

        const nextTop = Math.min(Math.max(start.top + (event.clientY - start.y), 0), maxTop);
        el.scrollTop = (nextTop / maxTop) * (el.scrollHeight - el.clientHeight);
      };

      const onUp = () => {
        drag.current = null;
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }, []);

    return (
      <div className='relative min-h-0 w-full max-md:flex-1'>
        <div
          ref={bindRef}
          className={cn(
            'vault-info-scrollbar max-md:absolute max-md:inset-0',
            canScroll ? 'overflow-y-auto' : 'overflow-y-hidden',
            className,
          )}
          style={maxHeight != null ? { maxHeight } : undefined}
        >
          {children}
        </div>

        {canScroll && thumb.visible && (
          <div
            className={cn(
              'pointer-events-none absolute top-2 right-[-10px] bottom-2 w-1 rounded-full bg-[var(--vault-info-scrollbar-track)] lg:right-[-22px]',
              trackClassName,
            )}
          >
            <div
              className={cn(
                'pointer-events-auto absolute right-0 left-0 rounded-full bg-[var(--vault-info-scrollbar-thumb)]',
                thumbClassName,
              )}
              style={{ top: thumb.top, height: thumb.height }}
              onMouseDown={(event) => {
                event.preventDefault();
                drag.current = { y: event.clientY, top: thumb.top };
              }}
            />
          </div>
        )}
      </div>
    );
  },
);
